import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { MAINTENANCE_RECORDS_LIST, MAINTENANCE_RECORD_DELETE } from '../../graphql/maintenanceRecords';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, CircularProgress, TextField, MenuItem, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Pagination from '../../components/Pagination';

const statusOptions = ['All', 'pending', 'completed'];

// Predefined maintenance categories for filtering
const maintenanceCategories = [
  { value: 'All', label: 'All Categories' },
  { value: 'oil_change', label: 'Oil Change' },
  { value: 'tire_rotation', label: 'Tire Rotation' },
  { value: 'battery_replacement', label: 'Battery Replacement' },
  { value: 'brake_service', label: 'Brake Service' },
  { value: 'air_filter', label: 'Air Filter Replacement' },
  { value: 'transmission_service', label: 'Transmission Service' },
  { value: 'coolant_flush', label: 'Coolant Flush' },
  { value: 'spark_plugs', label: 'Spark Plugs Replacement' },
  { value: 'fuel_filter', label: 'Fuel Filter Replacement' },
  { value: 'timing_belt', label: 'Timing Belt Replacement' },
  { value: 'suspension', label: 'Suspension Service' },
  { value: 'exhaust', label: 'Exhaust System' },
  { value: 'electrical', label: 'Electrical System' },
  { value: 'body_repair', label: 'Body Repair' },
  { value: 'glass_replacement', label: 'Glass Replacement' },
  { value: 'other', label: 'Other' }
];

// Helper function to get category display name
const getCategoryDisplayName = (categoryValue) => {
  const category = maintenanceCategories.find(cat => cat.value === categoryValue);
  return category ? category.label : categoryValue || 'Not Specified';
};

const MaintenanceList = ({ onEdit }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination and filter state from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = parseInt(searchParams.get('limit')) || 10;
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || 'All';
  const category = searchParams.get('category') || 'All';

  // Update URL parameters
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Handle filter changes
  const handleSearchChange = (value) => {
    updateURLParams({ search: value, page: 1 });
  };

  const handleStatusChange = (value) => {
    updateURLParams({ status: value, page: 1 });
  };

  const handleCategoryChange = (value) => {
    updateURLParams({ category: value, page: 1 });
  };

  const handlePageChange = (page) => {
    updateURLParams({ page });
  };

  const handleItemsPerPageChange = (limit) => {
    updateURLParams({ limit, page: 1 });
  };

  const { data, loading, error, refetch } = useQuery(MAINTENANCE_RECORDS_LIST, {
    variables: { 
      first: itemsPerPage,
      skip: (currentPage - 1) * itemsPerPage,
      sort: { createdAt: 'DESC' } 
    }
  });
  const [deleteMaintenance] = useMutation(MAINTENANCE_RECORD_DELETE, {
    onCompleted: () => refetch(),
  });

  const [deleteDialog, setDeleteDialog] = useState({ open: false, record: null });

  const handleDeleteClick = (record) => {
    setDeleteDialog({ open: true, record });
  };

  const handleDeleteConfirm = async () => {
    await deleteMaintenance({ variables: { filter: { id: deleteDialog.record.id } } });
    setDeleteDialog({ open: false, record: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, record: null });
  };

  const filteredRecords = useMemo(() => {
    if (!data?.maintenanceRecordsList?.items) return [];
    return data.maintenanceRecordsList.items.filter((r) => {
      const vehicleName = r.vehicle ? `${r.vehicle.make} ${r.vehicle.model}` : '';
      const matchesSearch =
        (r.type && r.type.toLowerCase().includes(search.toLowerCase())) ||
        (r.notes && r.notes.toLowerCase().includes(search.toLowerCase())) ||
        (r.description && r.description.toLowerCase().includes(search.toLowerCase())) ||
        (r.category && getCategoryDisplayName(r.category).toLowerCase().includes(search.toLowerCase())) ||
        vehicleName.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = status === 'All' || r.status === status;
      const matchesCategory = category === 'All' || r.category === category;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [data, search, status, category]);

  if (loading) return <CircularProgress />;
  if (error) return <div>Error loading maintenance records.</div>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
          placeholder="Search by category, description, or vehicle"
          size="small"
        />
        <TextField
          select
          label="Status"
          value={status}
          onChange={e => handleStatusChange(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          {statusOptions.map(option => (
            <MenuItem key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Category"
          value={category}
          onChange={e => handleCategoryChange(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {maintenanceCategories.map(option => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Vehicle</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Performed Date</TableCell>
              <TableCell>Scheduled Date</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecords.map((record) => (
              <TableRow key={record.id} hover onClick={() => onEdit(record)} sx={{cursor: 'pointer'}}>
                <TableCell>
                  {record.vehicle
                    ? `${record.vehicle.model ?? ''} ${record.vehicle.make ?? ''} - ${record.vehicle.licensePlate ?? ''}`.trim()
                    : record.maintenanceRecords
                      ? `${record.maintenanceRecords.model ?? ''} ${record.maintenanceRecords.make ?? ''} - ${record.maintenanceRecords.licensePlate ?? ''}`.trim()
                      : '-'}
                </TableCell>
                <TableCell>
                  <Box sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: record.category ? '#e3f2fd' : '#f5f5f5',
                    color: record.category ? '#1976d2' : '#666',
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}>
                    {getCategoryDisplayName(record.category)}
                  </Box>
                </TableCell>
                <TableCell>{record.description}</TableCell>
                <TableCell>{record.cost}</TableCell>
                <TableCell>{record.performedDate}</TableCell>
                <TableCell>{record.scheduledDate}</TableCell>
                <TableCell>{record.createdBy ? `${record.createdBy.firstName} ${record.createdBy.lastName}` : '-'}</TableCell>
                <TableCell>
                  <IconButton onClick={e => { e.stopPropagation(); onEdit(record); }} size="small" color="default" sx={{color: '#000'}}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={e => { e.stopPropagation(); handleDeleteClick(record); }} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil((data?.maintenanceRecordsList?.count || 0) / itemsPerPage)}
        totalItems={data?.maintenanceRecordsList?.count || 0}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        itemsPerPageOptions={[10, 25, 50, 100]}
      />
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Maintenance Record</DialogTitle>
        <DialogContent>Are you sure you want to delete this maintenance record?</DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceList; 